import React, { useState, useEffect } from 'react';
import useCrmStore from '../../../services/useCrmStore';
import { toast } from 'react-toastify';
import {
  FiX,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSave,
  FiMove,
  FiAlertCircle
} from 'react-icons/fi';
import '../CrmModals.css';

const StageManager = ({ onClose }) => {
  const {
    stages,
    createStage,
    updateStage,
    deleteStage,
    reorderStages,
    fetchStages,
    leads
  } = useCrmStore();

  const [localStages, setLocalStages] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', color: '' });
  const [isCreating, setIsCreating] = useState(false);
  const [newStageForm, setNewStageForm] = useState({ name: '', color: '' });
  const [draggedIndex, setDraggedIndex] = useState(null);


  useEffect(() => {
    setLocalStages([...stages].sort((a, b) => a.order - b.order));
  }, [stages]);

  const handleEdit = (stage) => {
    setEditingId(stage.id);
    setEditForm({ name: stage.name, color: stage.color });
  };

  const handleSaveEdit = async (stageId) => {
    try {
      await updateStage(stageId, editForm);
      toast.success('Stage atualizado com sucesso');
      setEditingId(null);
    } catch (err) {
      toast.error('Erro ao atualizar stage');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: '', color: '' });
  };

  const handleCreate = async () => {
    if (!newStageForm.name.trim()) {
      toast.error('Nome do stage é obrigatório');
      return;
    }

    try {
      const maxOrder = Math.max(...localStages.map(s => s.order), -1);
      await createStage({
        name: newStageForm.name,
        color: newStageForm.color,
        order: maxOrder + 1
      });
      toast.success('Stage criado com sucesso');
      setIsCreating(false);
      setNewStageForm({ name: '', color: '' });
    } catch (err) {
      toast.error('Erro ao criar stage');
    }
  };

  const handleDelete = async (stageId) => {
    try {
      await deleteStage(stageId);
      toast.success('Stage deletado com sucesso');
    } catch (err) {
      toast.error('Erro ao deletar stage');
    }
  };

  const handleDeleteClick = (stage) => {
    const stageHasLeads = (stage.leads && stage.leads.length > 0) || leads.some(lead => lead.stage_id === stage.id);

    if (stageHasLeads) {
      alert("Não é possível excluir uma stage que possui leads.");
      return;
    }

    if (window.confirm("Tem certeza que deseja excluir esta stage?")) {
      handleDelete(stage.id);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const updatedStages = [...localStages];
    const [draggedStage] = updatedStages.splice(draggedIndex, 1);
    updatedStages.splice(dropIndex, 0, draggedStage);


    const reorderedStages = updatedStages.map((stage, idx) => ({
      ...stage,
      order: idx
    }));

    setLocalStages(reorderedStages);
    setDraggedIndex(null);


    try {
      const stageOrders = reorderedStages.map((stage, idx) => ({
        id: stage.id,
        order: idx
      }));

      await reorderStages(stageOrders);
      toast.success('Ordem dos stages atualizada');
    } catch (err) {
      toast.error('Erro ao reordenar stages');
      console.error('Reorder error:', err);
      fetchStages();
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleMobileOrderChange = async (stageId, newOrder) => {
    const currentIndex = localStages.findIndex(s => s.id === stageId);
    if (currentIndex === -1) return;

    const updatedStages = [...localStages];
    const [movedStage] = updatedStages.splice(currentIndex, 1);
    updatedStages.splice(newOrder, 0, movedStage);

    const reorderedStages = updatedStages.map((stage, idx) => ({
      ...stage,
      order: idx
    }));

    setLocalStages(reorderedStages);

    try {
      const stageOrders = reorderedStages.map((stage, idx) => ({
        id: stage.id,
        order: idx
      }));

      await reorderStages(stageOrders);
      toast.success('Ordem dos stages atualizada');
    } catch (err) {
      toast.error('Erro ao reordenar stages');
      console.error('Reorder error:', err);
      fetchStages();
    }
  };

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div className="crm-modal crm-modal-small" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal-header">
          <div className="crm-modal-header-content">
            <h2 className="crm-modal-title">Gerenciar Stages do Pipeline</h2>
            <p className="crm-modal-subtitle">Reordene os stages ou clique para editar</p>
          </div>
          <button className="crm-modal-close" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className="crm-modal-body">
          <div className="stage-list">
            {localStages.map((stage, index) => (
              <div
                key={stage.id}
                className={`stage-item ${draggedIndex === index ? 'stage-item--dragging' : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                <div className="stage-item-drag-handle">
                  <FiMove />
                </div>

                {editingId === stage.id ? (
                  <>
                    <input
                      type="color"
                      value={editForm.color}
                      onChange={(e) => setEditForm({ ...editForm, color: e.target.value })}
                      className="stage-item-color-input"
                    />
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="stage-item-name-input"
                      autoFocus
                    />
                    <div className="stage-item-actions">
                      <button
                        className="stage-action-btn stage-action-btn--save"
                        onClick={() => handleSaveEdit(stage.id)}
                      >
                        <FiSave /> Salvar
                      </button>
                      <button
                        className="stage-action-btn stage-action-btn--cancel"
                        onClick={handleCancelEdit}
                      >
                        <FiX /> Cancelar
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className="stage-item-color"
                      style={{ backgroundColor: stage.color }}
                    />
                    <div className="stage-item-name">{stage.name}</div>
                    <div className="stage-item-order desktop-only">Ordem: {stage.order}</div>
                    <div className="stage-item-order mobile-only">
                      <select
                        value={index}
                        onChange={(e) => handleMobileOrderChange(stage.id, parseInt(e.target.value))}
                        onClick={(e) => e.stopPropagation()}
                        className="stage-mobile-order-select"
                      >
                        {localStages.map((_, idx) => (
                          <option key={idx} value={idx}>
                            {idx + 1}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="stage-item-actions">
                      <button
                        className="stage-action-btn stage-action-btn--edit"
                        onClick={() => handleEdit(stage)}
                      >
                        <FiEdit2 /> Editar
                      </button>
                      <button
                        className="stage-action-btn stage-action-btn--delete"
                        onClick={() => handleDeleteClick(stage)}
                      >
                        <FiTrash2 /> Deletar
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {isCreating ? (
            <div className="stage-item stage-item--new">
              <div className="stage-item-drag-handle crm-stage-item-drag-handle-faded">
                <FiMove />
              </div>
              <input
                type="color"
                value={newStageForm.color}
                onChange={(e) => setNewStageForm({ ...newStageForm, color: e.target.value })}
                className="stage-item-color-input"
              />
              <input
                type="text"
                value={newStageForm.name}
                onChange={(e) => setNewStageForm({ ...newStageForm, name: e.target.value })}
                className="stage-item-name-input"
                placeholder="Nome do novo stage..."
                autoFocus
              />
              <div className="stage-item-actions">
                <button
                  className="stage-action-btn stage-action-btn--save"
                  onClick={handleCreate}
                >
                  <FiSave /> Criar
                </button>
                <button
                  className="stage-action-btn stage-action-btn--cancel"
                  onClick={() => {
                    setIsCreating(false);
                    setNewStageForm({ name: '', color: '' });
                  }}
                >
                  <FiX /> Cancelar
                </button>
              </div>
            </div>
          ) : (
            <button
              className="stage-add-button"
              onClick={() => setIsCreating(true)}
            >
              <FiPlus /> Adicionar Novo Stage
            </button>
          )}
        </div>

        <div className="crm-modal-footer">
          <button className="crm-btn crm-btn-primary" onClick={onClose}>
            Concluído
          </button>
        </div>
      </div>
    </div>
  );
};

export default StageManager;
